﻿import "./SbbPersona.scss";

import classNames from "classnames";
import { getInitials } from "helpers";
import { createLogger } from "logging";
import { useApi } from "providers";
import React, { useEffect, useState } from "react";

const logger = createLogger("SbbPersona");

const profileImages = new Map();

export interface SbbPersonaProps {
  title: string;
  size?: number;
  subTitle?: string;
  profileImage?: string;
  initials?: string;
  initialsColor?: string;
  personnelNumber?: string;
  disabled?: boolean;
}

export const SbbPersona: React.FC<SbbPersonaProps> = ({
  title,
  subTitle,
  profileImage,
  size = 24,
  initials,
  initialsColor,
  disabled,
}) => {
  const { userApi } = useApi();
  title = `${title}`.replace(/null/g, "").trim();
  logger.trace("render():", title, initials, profileImage);

  initials = `${initials}`;
  if (!initials) {
    initials = getInitials(title, false, { firstInitialOnly: size <= 16 });
  }

  initialsColor = `${initialsColor}`;
  if (!initialsColor) initialsColor = "#0078d7";

  // Track previous profileImage to detect prop changes during render
  const [prevProfileImage, setPrevProfileImage] = useState(profileImage);
  const [imageBase64, setImageBase64] = useState<string | undefined>(() => {
    if (profileImage && profileImages.has(profileImage)) {
      return profileImages.get(profileImage);
    }
    return "";
  });

  // Synchronously adjust state when profileImage prop changes (during render, not in an effect).
  // This is the React-recommended pattern for deriving state from props.
  if (profileImage !== prevProfileImage) {
    setPrevProfileImage(profileImage);
    const cached = profileImage && profileImages.has(profileImage) ? profileImages.get(profileImage) : "";
    setImageBase64(cached);
  }

  // Only use an effect for the async fetch when image is not yet cached
  useEffect(() => {
    if (!profileImage || profileImages.has(profileImage)) return;

    userApi
      .getProfileImage(profileImage)
      .then((data) => {
        profileImages.set(profileImage, data);
        setImageBase64(data);
      })
      .catch(() => {
        profileImages.set(profileImage, null);
      });
  }, [profileImage, userApi]);

  const imageLoaded = imageBase64 && !imageBase64.endsWith("base64,");
  const wrapperClassName = classNames("ms-Persona", `ms-Persona--${size}`, disabled ? "ms-Persona--Disabled" : "");
  const imageAreaClassName = classNames("ms-Persona-imageArea", imageLoaded ? "image-loaded" : "");

  return (
    <div className={wrapperClassName}>
      <div className={imageAreaClassName}>
        <div className="ms-Persona-initials" title={title} style={{ backgroundColor: initialsColor }}>
          {initials}
        </div>
        {imageLoaded && (
          <div className="ms-Persona-image">
            <img aria-hidden="true" className="persona-image" src={imageBase64} alt={title} />
          </div>
        )}
      </div>
      <div className="ms-Persona-title">{title}</div>
      <div className="ms-Persona-secondaryText">{subTitle}</div>
    </div>
  );
};
